import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { BIOMARKER_PANELS } from '../data/constants';
import { getReferenceRanges, extractBiomarkers, saveBiomarkers, analyzeBiomarkers } from '../lib/api';
import { useUserStore } from '../store/userStore';

export function ClinicianBiomarkerUpload({ patientId, onSaved, onUploaded }) {
  const staff = useUserStore((s) => s.user);
  const [ranges, setRanges] = useState({});
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState(null); // { values, testDate, note, prefilled: [] }
  const fileRef = useRef(null);

  useEffect(() => {
    getReferenceRanges().then(setRanges).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setExtracting(true);
    try {
      const res = await extractBiomarkers(file, { patientId, uploadedBy: staff?.id });
      const extracted = res.extracted || {};
      const keys = Object.keys(extracted);
      if (keys.length === 0) {
        toast.error('No results recognised. Try a clearer file or enter manually.');
        onUploaded?.();
        return;
      }
      setReview({
        uploadId: res.upload_id,
        values: Object.fromEntries(keys.map((k) => [k, String(extracted[k])])),
        testDate: res.test_date || new Date().toISOString().split('T')[0],
        note: res.notes || '',
        prefilled: keys,
      });
      toast.success(`Extracted ${keys.length} result${keys.length > 1 ? 's' : ''} — please review`);
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read the file. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  const setVal = (key, v) => setReview((p) => ({ ...p, values: { ...p.values, [key]: v } }));

  const save = async () => {
    const results = {};
    Object.entries(review.values).forEach(([k, v]) => {
      if (v !== '' && v !== null && !isNaN(parseFloat(v))) results[k] = parseFloat(v);
    });
    if (Object.keys(results).length === 0) {
      toast.error('Enter at least one result');
      return;
    }
    setSaving(true);
    try {
      const saved = await saveBiomarkers({ patient_id: patientId, test_date: review.testDate, test_type: 'comprehensive_panel', results, entered_by: staff?.id, upload_id: review.uploadId || null });
      toast.success('Biomarker results saved');
      setReview(null);
      onSaved?.();
      // Run AI analysis in the background so the clinician isn't blocked
      analyzeBiomarkers(saved.biomarker_id)
        .then(() => onSaved?.())
        .catch(() => {});
    } catch (e) {
      toast.error('Could not save results. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <input ref={fileRef} data-testid="clinician-biomarker-upload-input" type="file" accept="image/*,application/pdf,.pdf" onChange={onFile} className="hidden" />
      <button
        data-testid="clinician-biomarker-upload-btn"
        onClick={() => fileRef.current?.click()}
        disabled={extracting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl aura-btn-gradient text-white font-semibold px-4 py-2.5 disabled:opacity-70"
      >
        {extracting ? <><i className="fa-solid fa-circle-notch fa-spin" /> Reading results…</> : <><i className="fa-solid fa-wand-magic-sparkles" /> Upload results (AI fills in)</>}
      </button>

      {review ? (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="biomarker-review-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !saving && setReview(null)} />
          <div className="relative w-full sm:max-w-lg max-h-[90vh] overflow-y-auto glass-dark rounded-t-3xl sm:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl text-white">Review extracted results</h3>
              <button onClick={() => !saving && setReview(null)} className="grid place-items-center w-8 h-8 rounded-full bg-white/10 text-white"><i className="fa-solid fa-xmark" /></button>
            </div>
            <p className="text-sm text-white/60 mb-3">AI read these from the report. Check each value before saving to the patient's record.</p>

            <label className="block text-xs text-white/50 mb-1">Test date</label>
            <input type="date" value={review.testDate} onChange={(e) => setReview((p) => ({ ...p, testDate: e.target.value }))} className="w-full mb-4 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none" />

            {review.note ? (
              <p className="text-xs text-white/70 mb-4 bg-white/5 rounded-lg px-3 py-2 border border-white/10">
                <i className="fa-solid fa-circle-info mr-1.5 text-[var(--aura-purple-light)]" />{review.note}
              </p>
            ) : null}

            {BIOMARKER_PANELS.map((panel) => {
              const active = panel.markers.filter((m) => review.prefilled.includes(m) || (review.values[m] ?? '') !== '');
              if (active.length === 0) return null;
              return (
                <div key={panel.key} className="mb-4">
                  <h4 className="text-sm font-semibold text-white/80 mb-2">{panel.key}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {active.map((m) => {
                      const ref = ranges[m];
                      return (
                        <div key={m}>
                          <label className="block text-[11px] text-white/50 mb-1">{ref?.name || m}{ref ? ` (${ref.unit})` : ''}</label>
                          <input
                            data-testid={`review-biomarker-${m}`}
                            type="number"
                            step="any"
                            value={review.values[m] ?? ''}
                            onChange={(e) => setVal(m, e.target.value)}
                            className="w-full rounded-xl bg-white/10 text-white border border-[var(--aura-purple-light)]/50 px-3 py-2 outline-none"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <button
              data-testid="save-extracted-biomarkers-btn"
              onClick={save}
              disabled={saving}
              className="w-full mt-2 aura-btn-gradient text-white font-semibold rounded-2xl py-3.5 flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {saving ? <><i className="fa-solid fa-circle-notch fa-spin" /> Saving…</> : <><i className="fa-solid fa-floppy-disk" /> Save to patient record</>}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
